import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";

// hooks and utilities
import useRedirect from "hooks/useRedirect";
import { formatMoney } from "common/format";
import useTabId from "hooks/useTabId";
import useFleet from "hooks/useFleet";
import useTab from "hooks/useTab";

//classes
import Aircraft from "classes/aircraft";
import Route from "classes/route";

// common components
import Select from "react-select";
import SlidingTextButton from "components/common/SlidingTextButton/SlidingTextButton";

//api
import api from "api/api";

// assets
import resourceIcon from "assets/icons/adminTools/resource.svg";

//scss
import "./AdminTools.scoped.scss";

const processVal = str => str.replace(/\D/g, "");
const AdminTools = () => {
	const [tab, setTab] = useState(0); // 0 - overview; 1 - resources; 2 - routes; 3 - airplanes
	const dispatch = useDispatch();

	const getUserInfo = useCallback(async () => {
		try {
			const { data } = await axios.get(`${api}/user/tester`);
			if (!data.tester) {
				dispatch({ type: "CHANGE_BOX_TAB", tab: null });
			}
		} catch (err) {
			console.error(err);
		}
	}, [dispatch]);

	useEffect(() => {
		getUserInfo();
	}, [getUserInfo]);

	return (
		<div>
			{tab !== 0 && (
				<SlidingTextButton
					icon={<i className="fas fa-arrow-left" />}
					text={"Wróć"}
					onClick={() => setTab(0)}
					background={"darkBlue"}
				/>
			)}

			{tab === 0 && (
				<div className="content">
					<div className="tile" onClick={() => setTab(1)}>
						<img src={resourceIcon} alt="Zasoby" />
						Zasoby
					</div>

					<div className="tile" onClick={() => setTab(3)}>
						<i className="fas fa-plane" />
						Samoloty
					</div>

					<div className="tile" onClick={() => setTab(2)}>
						<i className="fas fa-route" />
						Trasy
					</div>
				</div>
			)}
			{tab === 1 && <Resources />}
			{tab === 2 && <Routes />}
			{tab === 3 && <Airplanes />}
		</div>
	);
};

const Resources = () => {
	const [moneyToSet, setMoneyToSet] = useState(1000000);
	const [moneyString, setMoneyString] = useState("1 000 000 USD");
	const [moneyLabel, setMoneyLabel] = useState(0); // 0 - input value; 1 - correct value; 2 - limit exceeded
	const [pointsToSet, setPointsToSet] = useState(0);
	const [pointsLabel, setPointsLabel] = useState(0); // 0 - input value; 1 - correct value; 2 - limit exceeded
	const dispatch = useDispatch();
	const moneyInputRef = useRef();

	const updatePointsToAdd = newValue => setPointsToSet(processVal(newValue));
	const addMoney = () => addResources("money", +moneyToSet);
	const addPoints = () => addResources("points", +pointsToSet);
	const setCursor = (pos, ref) => {
		if (!ref.current) return;

		try {
			const tag = ref.current;
			tag.focus();
			tag.setSelectionRange(pos, pos);
		} catch (e) {}
	};

	const addResources = async (type, amount) => {
		if ((type === "money" && moneyLabel !== 1) || (type === "points" && pointsLabel !== 1)) {
			alert("Niepoprawna wartość");
			return;
		}

		try {
			const { data } = await axios.patch(`${api}/group/resources`, {
				money: type === "money" ? amount : undefined,
				points: type === "points" ? amount : undefined
			});
			dispatch({ type: type === "points" ? "SET_POINTS" : "SET_MONEY", state: data.amount });
		} catch (e) {
			console.error(e);
		}
	};

	useEffect(() => {
		setMoneyString(formatMoney(moneyToSet));

		if (moneyToSet === 0) setMoneyLabel(0);
		if (moneyToSet > 0 && moneyToSet <= 20000000000) setMoneyLabel(1);
		if (moneyToSet > 20000000000) setMoneyLabel(2);
	}, [moneyToSet]);

	useEffect(() => {
		setCursor(moneyString.length - 4, moneyInputRef);
	}, [moneyString]);

	useEffect(() => {
		if (pointsToSet === 0) setPointsLabel(0);
		if (pointsToSet > 0 && pointsToSet <= 500) setPointsLabel(1);
		if (pointsToSet > 500 || pointsToSet === "") setPointsLabel(2);
	}, [pointsToSet]);

	return (
		<div className="content resources">
			<div className="item">
				<div className="label">Pieniądze</div>
				<div className="flex">
					<input
						className="input search"
						type="text"
						value={moneyString}
						onChange={e => setMoneyToSet(processVal(e.target.value))}
						ref={moneyInputRef}
					/>
					<div className={`input__label ${moneyLabel === 1 && "green"} ${moneyLabel === 2 && "red"}`}>
						{moneyLabel === 0 && "Wpisz ilość"}
						{moneyLabel === 1 && "Poprawna wartość"}
						{moneyLabel === 2 && "Za duża wartość"}
					</div>
					<button className="button--primary circle_button" onClick={addMoney}>
						<i className="fa fa-plus" />
					</button>
				</div>
			</div>

			<div className="item">
				<div className="label">Punkty</div>
				<div className="flex">
					<input
						className="input search"
						type="text"
						value={pointsToSet}
						onChange={e => updatePointsToAdd(processVal(e.target.value))}
					/>
					<div className={`input__label ${pointsLabel === 1 && "green"} ${pointsLabel === 2 && "red"}`}>
						{pointsLabel === 0 && "Wpisz ilość"}
						{pointsLabel === 1 && "Poprawna wartość"}
						{pointsLabel === 2 && "Za duża lub niepoprawna wartość"}
					</div>
					<button className="button--primary circle_button" onClick={addPoints}>
						<i className="fa fa-plus" />
					</button>
				</div>
			</div>
		</div>
	);
};

const Routes = () => {
	const flightPlans = useSelector(state => state.flightPlans);

	const deleteAllRoutes = () => {
		const confirmation = window.confirm("Czy na pewno chcesz usunąć wszystkie trasy? To może zepsuć grę!");
		if (!confirmation) return;

		Route.deleteAll();
	};

	const deleteAllFlightPlans = () => {
		const confirmation = window.confirm("Usunięte zostaną wszystkie plany lotu! Czy na pewno chcesz kontynuować?");
		if (!confirmation) return;

		flightPlans.map(p => p.delete(false)); // TODO: optimize -> create new api endpoint for deleting all flight plans
	};

	return (
		<div className="content routes">
			<SlidingTextButton
				text={"Usuń wszystkie trasy (na mapie)"}
				icon={<i className="fas fa-trash" />}
				background={"darkBlue"}
				onClick={deleteAllRoutes}
			/>

			<SlidingTextButton
				text={"Usuń wszystkie plany lotów"}
				icon={<i className="fas fa-trash" />}
				background={"darkBlue"}
				onClick={deleteAllFlightPlans}
			/>
		</div>
	);
};

const Airplanes = () => {
	const [selectedAircraft, setSelectedAircraft] = useState(null);
	const [newSpeedString, setNewSpeedString] = useState("");
	const [newSpeed, setNewSpeed] = useState(0);
	const [speedLabel, setSpeedLabel] = useState(0); // 0 - input value; 1 - correct value; 2 - limit exceeded
	const fleet = useFleet();
	const staff = useSelector(state => state.staff);
	const routes = useSelector(state => state.routes);
	const dispatch = useDispatch();

	const fleetTab = useTab("aircraftIcon.fleet");
	const windowAircraftIdent = useTabId(`aircraftIcon.fleet.${fleetTab}.details`);
	const redirect = useRedirect();

	const options = fleet.map(value => ({ value: value.ident, label: value.registration }));

	const deleteAllAircraft = () => {
		const confirmation = window.confirm(
			"Czy na pewno chcesz usunąć całą flotę? Sprawi to też usunięcie wszystkich tras!"
		);
		if (!confirmation) return;

		fleet.forEach(aircraft => (aircraft.showedInPopup = false));
		Aircraft.deleteAll();
	};

	const deleteSelectedAircraft = async () => {
		const confirmation = window.confirm(
			`Czy na pewno chcesz usunąć ten samolot? Sprawi to też usunięcie wszystkich tras, do których przypisany jest tylko ten samolot!`
		);
		if (!confirmation || !selectedAircraft) return;
		const { value: aircraftIdent } = selectedAircraft;

		routes.forEach(route => {
			if (!route.aircraft.includes(aircraftIdent)) return;

			route.removeAircraft(aircraftIdent);
		});

		await Aircraft.get(aircraftIdent).removeFromMap();

		setTimeout(async () => {
			try {
				await axios.delete(`${api}/aircraft/${aircraftIdent}`);
			} catch (e) {
				console.error(e);
				return;
			}

			if (windowAircraftIdent === aircraftIdent) {
				redirect("aircraftIcon.fleet");
			}

			const newFleet = fleet.filter(aircraft => (aircraft.ident === aircraftIdent ? false : true));
			staff.forEach(s => s.aircraft === aircraftIdent && s.setAircraft(null));

			dispatch({ type: "SET_FLEET", payload: newFleet });
			dispatch({ type: "UPDATE_STAFF" });
		}, 20);

		setSelectedAircraft(null);
		fleet.forEach(aircraft => (aircraft.showedInPopup = false));
		setTimeout(() => dispatch({ type: "AIRCRAFT_POPUP_SET_AIRCRAFT", state: null }), 1300);
	};

	const changeAircraftSpeed = () => {
		const { value: aircraftIdent } = selectedAircraft;
		const aircraft = fleet.find(a => a.ident === aircraftIdent);
		aircraft.specs.speed = [newSpeed, newSpeed * 0.54];

		const newFleet = fleet.map(a => (a.ident === aircraftIdent ? aircraft : a));
		dispatch({ type: "FLEET_SET", state: newFleet });
		Aircraft.saveFleet(newFleet);

		alert("Pomyślnie zmieniono prędkość samolotu");
		setNewSpeedString("");
	};

	useEffect(() => {
		const processedValue = processVal(newSpeedString);
		setNewSpeed(processedValue.length > 0 ? processedValue : "");
	}, [newSpeedString]);

	useEffect(() => {
		if (newSpeed === "") setSpeedLabel(0);
		if (newSpeed > 0 && newSpeed <= 10000) setSpeedLabel(1);
		if (newSpeed > 10000 || newSpeed === 0) setSpeedLabel(2);
	}, [newSpeed]);

	return (
		<div className="content airplanes">
			<SlidingTextButton
				icon={<i className="fas fa-trash" />}
				text="Usuń wszystkie samoloty"
				onClick={deleteAllAircraft}
				background={"darkBlue"}
			/>

			<hr />

			{fleet.length > 0 && (
				<>
					<div className="label select__label">Wybierz samolot</div>
					<Select
						className="react_select"
						classNamePrefix="react_select"
						options={options}
						placeholder="---"
						value={selectedAircraft}
						onChange={setSelectedAircraft}
					/>
				</>
			)}

			{selectedAircraft && (
				<>
					<div className="item">
						<div className="label">Zmień prędkość</div>
						<div className="flex">
							<input
								className="input search"
								type="text"
								value={newSpeedString}
								onChange={e => setNewSpeedString(e.target.value)}
							/>
							<div className={`input__label ${speedLabel === 1 && "green"} ${speedLabel === 2 && "red"}`}>
								{speedLabel === 0 && "Wpisz nową prędkość"}
								{speedLabel === 1 && "Poprawna wartość"}
								{speedLabel === 2 && "Za duża wartość"}
							</div>
							<button className="button--primary circle_button" onClick={changeAircraftSpeed}>
								<i className="fa fa-plus" />
							</button>
						</div>
					</div>

					<SlidingTextButton
						icon={<i className="fas fa-trash" />}
						text="Usuń wybrany samolot"
						onClick={deleteSelectedAircraft}
						background={"darkBlue"}
					/>
				</>
			)}

			{fleet.length <= 0 && <div className="empty">Brak samolotów</div>}
		</div>
	);
};

export default AdminTools;
